import ConfirmDialog from '@components/Common/ConfirmDialog';
import { SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import SelectFormField from '@components/Common/SelectFormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import {
  createPlacementFeeMutation,
  deletePlacementFeeMutation,
  getPlacementFeeByApplicationQuery,
  updatePlacementFeeMutation,
} from '@constants/graphQLQueries';
import type { PlacementFeeBasis, PlacementFeeInput } from '@constants/graphQLQueries';
import { SALARY_CURRENCIES } from '@constants/salary';
import dayjs from '@helpers/date';
import { fmtMoney } from '@helpers/money';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { formatDate } from '@helpers/date';

interface Fee {
  _id: string;
  /** ONE_MONTH / HALF_MONTH; FIXED / PERCENT_OF_CTC on fees recorded before the salary terms. */
  basis: string;
  monthlySalary: number | null;
  amount: number;
  percent: number | null;
  currency: string;
  hiredAt: string | null;
  notes: string | null;
}

interface Draft {
  basis: PlacementFeeBasis;
  monthlySalary: string;
  currency: string;
  hiredAt: string;
  notes: string;
}

const BASIS_OPTIONS = [
  { value: 'ONE_MONTH', label: "One month's salary" },
  { value: 'HALF_MONTH', label: "Half a month's salary" },
];

const FACTOR: Record<PlacementFeeBasis, number> = { ONE_MONTH: 1, HALF_MONTH: 0.5 };

const basisLabel = (f: Fee) => {
  if (f.basis === 'ONE_MONTH') return "One month's salary";
  if (f.basis === 'HALF_MONTH') return "Half a month's salary";
  if (f.basis === 'PERCENT_OF_CTC') return `${f.percent}% of CTC (older terms)`;
  return 'Fixed amount (older terms)';
};

/**
 * What Mintly earned for this placement.
 *
 * Managed hiring is paid when the candidate joins: the client pays one month of the candidate's
 * salary, or half a month. So the fee is recorded as those terms plus the monthly salary at
 * joining, and the backend works out the amount; the preview here is the same arithmetic. It is
 * credited to the job's assigned HR, which is how the Revenue tab shows what each HR brings in.
 *
 * Shown once the candidate is in the Selected stage. A Super Admin records and edits it; an HR
 * sees it read-only.
 */
const PlacementFeeCard: React.FC<{
  applicationId: string;
  canEdit: boolean;
  /** The offer on this application, shown as a reminder while entering the salary. */
  offeredSalary?: number | null;
  offeredCurrency?: string;
}> = ({ applicationId, canEdit, offeredSalary, offeredCurrency }) => {
  const { showToast } = useToast();
  const [fee, setFee] = useState<Fee | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(() => {
    fetchGraphQLApiData(getPlacementFeeByApplicationQuery(applicationId))
      .then((r) => setFee(r?.data?.getPlacementFeeByApplication ?? null))
      .catch(() => setFee(null));
  }, [applicationId]);

  useEffect(load, [load]);

  const startEdit = () => {
    setDraft({
      basis: fee?.basis === 'HALF_MONTH' ? 'HALF_MONTH' : 'ONE_MONTH',
      monthlySalary: fee?.monthlySalary != null ? String(fee.monthlySalary) : '',
      currency: fee?.currency ?? offeredCurrency ?? 'INR',
      hiredAt: fee?.hiredAt ? dayjs(fee.hiredAt).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      notes: fee?.notes ?? '',
    });
    setEditing(true);
  };

  const set = (k: keyof Draft, v: string) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  const save = async () => {
    if (!draft) return;
    const salary = draft.monthlySalary.trim() === '' ? null : Number(draft.monthlySalary);
    const input: PlacementFeeInput = {
      applicationId,
      basis: draft.basis,
      monthlySalary: salary,
      currency: draft.currency,
      hiredAt: draft.hiredAt || null,
      notes: draft.notes || null,
    };
    try {
      const res = await fetchGraphQLApiData(
        fee ? updatePlacementFeeMutation(fee._id, input) : createPlacementFeeMutation(input),
      );
      const saved = res?.data?.createPlacementFee ?? res?.data?.updatePlacementFee;
      if (!saved) {
        showToast('The fee was not saved.', 'error');
        return;
      }
      setFee(saved);
      setEditing(false);
      showToast('Placement fee saved', 'success');
    } catch (e: any) {
      // The backend's refusals are written for this screen ("Enter the candidate's monthly
      // salary at joining."), so they are shown as-is, minus the helper's prefix.
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'The fee was not saved.', 'error');
    }
  };

  const remove = async () => {
    if (!fee) return;
    try {
      await fetchGraphQLApiData(deletePlacementFeeMutation(fee._id));
      setFee(null);
      showToast('Placement fee removed', 'success');
    } catch {
      showToast('The fee was not removed.', 'error');
    } finally {
      setConfirmDelete(false);
    }
  };

  const salary = draft ? Number(draft.monthlySalary) : NaN;
  const preview = draft && salary > 0 ? Math.round(salary * FACTOR[draft.basis] * 100) / 100 : null;

  return (
    <SectionCard
      title="Placement fee"
      icon={<HandshakeOutlinedIcon />}
      meta={canEdit ? 'Super Admin' : 'Read only'}
      editing={editing}
      onEdit={canEdit ? startEdit : undefined}
      onCancel={() => setEditing(false)}
      onSave={save}
    >
      {editing && draft ? (
        <>
          <WorkspaceField
            label="Terms"
            editing
            edit={(
              <SelectFormField
                required={false}
                name="feeBasis"
                label=""
                value={draft.basis}
                options={BASIS_OPTIONS}
                onChange={(_n, v) => set('basis', String(v))}
                selectProps={{ inputProps: { 'aria-label': 'Fee terms' } }}
              />
            )}
          />
          <WorkspaceField
            label="Monthly salary at joining"
            editing
            edit={(
              <WorkspaceInput
                type="number"
                value={draft.monthlySalary}
                onChange={(v) => set('monthlySalary', v)}
                helperText={
                  preview != null
                    ? `Fee: ${fmtMoney(preview, draft.currency)}`
                    : offeredSalary
                      ? `Offer on this application: ${fmtMoney(offeredSalary, offeredCurrency)}`
                      : undefined
                }
              />
            )}
          />
          <WorkspaceField
            label="Currency"
            editing
            edit={(
              <SelectFormField
                required={false}
                name="feeCurrency"
                label=""
                value={draft.currency}
                options={SALARY_CURRENCIES}
                onChange={(_n, v) => set('currency', String(v))}
                selectProps={{ inputProps: { 'aria-label': 'Fee currency' } }}
              />
            )}
          />
          <WorkspaceField
            label="Joined on"
            editing
            edit={<WorkspaceInput type="date" value={draft.hiredAt} onChange={(v) => set('hiredAt', v)} />}
          />
          <WorkspaceField
            label="Notes"
            full
            editing
            edit={<WorkspaceInput value={draft.notes} onChange={(v) => set('notes', v)} />}
          />
        </>
      ) : fee ? (
        <>
          <WorkspaceField label="Fee" value={fmtMoney(fee.amount, fee.currency)} mono />
          <WorkspaceField label="Terms" value={basisLabel(fee)} />
          {fee.monthlySalary != null && (
            <WorkspaceField label="Monthly salary" value={fmtMoney(fee.monthlySalary, fee.currency)} mono />
          )}
          <WorkspaceField label="Joined on" value={formatDate(fee.hiredAt, '')} mono />
          <WorkspaceField label="Notes" full value={fee.notes ?? ''} />
          {canEdit && (
            <Box className="cc-field--full">
              <Button size="small" color="error" onClick={() => setConfirmDelete(true)}>
                Remove fee
              </Button>
            </Box>
          )}
        </>
      ) : (
        <WorkspaceField
          label="Fee"
          full
          value={canEdit ? 'Not recorded yet. Use Edit to record it.' : 'Not recorded yet.'}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Remove this placement fee?"
        body="It comes off the revenue dashboard and off the HR's total. You can record it again."
        actionLabel="Remove fee"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={remove}
      />
    </SectionCard>
  );
};

export default PlacementFeeCard;
